/**
 * PrivacyContext — owns the "hide all my investment figures" toggle.
 *
 * Surface area (Phase 1 + 2 + 4):
 *  - `isMasked`             : current visual state
 *  - `togglePrivacy()`      : flip the toggle (handles re-auth on UNMASK)
 *  - `forceMask()`          : programmatic mask without re-auth (used by
 *                             visibilitychange + idle timer)
 *  - `requireAuth`          : user preference — re-authenticate to reveal
 *  - `setRequireAuth(bool)` : persist preference
 *  - `idleTimeoutMin`       : 0 = never; 1/3/5/10/15 are accepted
 *  - `setIdleTimeout(mins)` : persist preference
 *  - `instantMaskOnBlur`    : true (per user spec — non-configurable)
 *  - `reAuthOpen / setReAuthOpen` : wired into the ReAuthDialog
 *  - `onReAuthSuccess()`    : called by ReAuthDialog after server verifies
 *
 * Storage:
 *  - `equitrack:privacy_masked` (sessionStorage)        — current mask state
 *  - `equitrack:privacy_require_auth` (localStorage)    — user preference
 *  - `equitrack:privacy_idle_timeout_min` (localStorage)— minutes (0=never)
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { setPrivateMode, isPrivateMode } from '../utils/formatCurrency';
import { useAuth } from './AuthContext';

const PrivacyContext = createContext(null);

const SESSION_KEY = 'equitrack:privacy_masked';
const LS_REQUIRE_AUTH = 'equitrack:privacy_require_auth';
const LS_IDLE_TIMEOUT = 'equitrack:privacy_idle_timeout_min';

export const PrivacyProvider = ({ children }) => {
  // Iter-135 (2026-06-03): Privacy mode is a feature for AUTHENTICATED
  // users protecting their own financial figures from over-the-shoulder
  // snooping. For anonymous visitors browsing the public marketing pages
  // (/, /pricing, /features, /about, /terms etc.) it is structurally
  // meaningless — they have no financial data to mask, and crucially
  // they have no password to satisfy the re-auth dialog with. Before
  // this guard, a first-time visitor landing on /pricing would see
  // masked plan prices AND a ReAuthDialog asking them to type a
  // password they don't have. We read auth here (AuthProvider wraps
  // PrivacyProvider in App.js so this is safe).
  const { user } = useAuth();
  const isAnonymous = !user;

  // Default to MASKED on every fresh session (user spec 2026-05-25 —
  // when the user is in public, the *safer* default is "hidden" and they
  // intentionally unmask after authenticating). The session-storage key
  // means refreshing the page within the same session preserves the
  // current state (no re-auth nag after every page nav).
  const [isMasked, setIsMasked] = useState(() => {
    try {
      const v = sessionStorage.getItem(SESSION_KEY);
      // No session value yet → start MASKED. Subsequent renders within the
      // same session honour whatever the user last toggled to.
      if (v === null) return true;
      return v === '1';
    } catch (_) { return true; }
  });

  const [requireAuth, setRequireAuthState] = useState(() => {
    try {
      const v = localStorage.getItem(LS_REQUIRE_AUTH);
      return v === null ? true : v === '1';  // default ON for safety
    } catch (_) { return true; }
  });

  const [idleTimeoutMin, setIdleTimeoutMinState] = useState(() => {
    try {
      const n = parseInt(localStorage.getItem(LS_IDLE_TIMEOUT) || '5', 10);
      return Number.isFinite(n) && n >= 0 ? n : 5;
    } catch (_) { return 5; }
  });

  const [reAuthOpen, setReAuthOpen] = useState(false);
  const pendingUnmaskRef = useRef(false);  // true when the user clicked "unmask" and we showed re-auth

  // Long-press temporary reveal — a Set of stable string keys that should
  // render unmasked even while `isMasked === true`. Each reveal auto-expires
  // after a per-call timeout (default 3 s). Used by `<MaskedFigure>` for
  // the long-press-to-peek gesture on individual numbers.
  // Stored as a `Set` and bumped via `version` so memo consumers re-render.
  const revealedKeysRef = useRef(new Set());
  const [revealVersion, setRevealVersion] = useState(0);
  const revealTimersRef = useRef({});
  const revealKey = useCallback((key, ms = 3000) => {
    if (!key) return;
    revealedKeysRef.current.add(key);
    setRevealVersion((v) => v + 1);
    // Clear any existing timer so a second long-press extends, not stacks.
    if (revealTimersRef.current[key]) {
      clearTimeout(revealTimersRef.current[key]);
    }
    revealTimersRef.current[key] = setTimeout(() => {
      revealedKeysRef.current.delete(key);
      delete revealTimersRef.current[key];
      setRevealVersion((v) => v + 1);
    }, ms);
  }, []);
  const isKeyRevealed = useCallback((key) => {
    // revealVersion is read so consumers re-render when the Set changes.
    return revealVersion >= 0 && revealedKeysRef.current.has(key);
  }, [revealVersion]);

  // Synchronous mask setter — does THREE things in the same tick:
  //   1. Adds a `privacy-masked` class to <body> so a CSS rule can hide
  //      every figure INSTANTLY (one DOM write — paint completes in the
  //      same frame as the click). This is what makes the toggle feel
  //      snappy on iPad / mobile Safari even when there are many
  //      holdings + charts in the tree.
  //   2. Flips the module-level _privateMode flag so formatINR /
  //      formatQuantity / formatPercentage return the bullet string the
  //      next time they're called.
  //   3. Updates React state so subscribers (XirrBadge, etc) re-render
  //      with the new value. The CSS in step 1 has already given the
  //      user a visual confirmation, so the React work can complete in
  //      the background without feeling slow.
  const applyMask = useCallback((next) => {
    const v = !!next;
    if (typeof document !== 'undefined') {
      // Toggle the class first so the browser paints the masked state
      // before React's reconciliation even starts.
      if (v) document.body.classList.add('privacy-masked');
      else document.body.classList.remove('privacy-masked');
    }
    setPrivateMode(v);
    try { sessionStorage.setItem(SESSION_KEY, v ? '1' : '0'); } catch (_) { /* ignore */ }
    setIsMasked(v);
  }, []);

  // Initial sync on mount — flag may have been seeded from sessionStorage
  // by the useState lazy-initialiser; make sure formatCurrency AND the
  // body class are in lockstep with the initial isMasked value, so that
  // privacy mode renders correctly on page load (without needing a click).
  useEffect(() => {
    setPrivateMode(isMasked);
    if (typeof document !== 'undefined') {
      if (isMasked) document.body.classList.add('privacy-masked');
      else document.body.classList.remove('privacy-masked');
    }
    // Run once on mount only. Subsequent updates go through applyMask().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter-135: whenever auth state flips to anonymous (logout, fresh
  // visitor) we must force-unmask so public pages render correctly.
  // We also close any open re-auth dialog so the visitor isn't stuck
  // staring at a password prompt.
  useEffect(() => {
    if (isAnonymous) {
      setPrivateMode(false);
      if (typeof document !== 'undefined') {
        document.body.classList.remove('privacy-masked');
      }
      setIsMasked(false);
      try { sessionStorage.setItem(SESSION_KEY, '0'); } catch (_) { /* ignore */ }
      setReAuthOpen(false);
      pendingUnmaskRef.current = false;
    }
  }, [isAnonymous]);

  // Persist user preferences.
  const setRequireAuth = useCallback((v) => {
    setRequireAuthState(!!v);
    try { localStorage.setItem(LS_REQUIRE_AUTH, v ? '1' : '0'); } catch (_) { /* ignore */ }
  }, []);

  const setIdleTimeout = useCallback((mins) => {
    setIdleTimeoutMinState(mins);
    try { localStorage.setItem(LS_IDLE_TIMEOUT, String(mins)); } catch (_) { /* ignore */ }
  }, []);

  // Programmatic mask (visibility-change + idle).
  const forceMask = useCallback(() => {
    applyMask(true);
  }, [applyMask]);

  // Toggle handler. Masking is always free; UNMASKING goes through the
  // re-auth dialog when `requireAuth` is on. Iter-135: anonymous visitors
  // have no financial figures to mask and no password to re-auth with,
  // so the toggle is a hard no-op for them — never even opens the
  // ReAuthDialog (which would ask for a non-existent password).
  const togglePrivacy = useCallback(() => {
    if (isAnonymous) return;
    if (isMasked) {
      if (requireAuth) {
        pendingUnmaskRef.current = true;
        setReAuthOpen(true);
        return;
      }
      applyMask(false);
    } else {
      applyMask(true);
    }
  }, [isAnonymous, isMasked, requireAuth, applyMask]);

  // Global keyboard shortcut: Shift + E → toggle privacy mask.
  // We listen at the window level so the shortcut works from any focus
  // context, but bail out the moment the user is typing in an input,
  // textarea, contenteditable, or has the ReAuthDialog / any other modal
  // open (capturing E inside a "search by name" input would be hostile).
  useEffect(() => {
    const isTyping = (target) => {
      if (!target) return false;
      const tag = (target.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (target.isContentEditable) return true;
      return false;
    };
    const onKey = (e) => {
      // We only care about plain `Shift + E` — bail if any other modifier
      // is held so we don't clash with Shift+Cmd+E, Shift+Ctrl+E etc.
      if (!e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.key || '').toUpperCase() !== 'E') return;
      if (isTyping(e.target)) return;
      // Don't fire while a modal/dialog is open — would feel like the
      // keystroke "fell through" and unmasked behind the dialog.
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      e.preventDefault();
      togglePrivacy();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePrivacy]);

  const onReAuthSuccess = useCallback(() => {
    pendingUnmaskRef.current = false;
    setReAuthOpen(false);
    applyMask(false);
  }, [applyMask]);

  const onReAuthCancel = useCallback(() => {
    pendingUnmaskRef.current = false;
    setReAuthOpen(false);
  }, []);

  // Phase 4: auto-mask on tab/window blur (INSTANT per user spec) and on
  // idle (configurable). Both no-op when already masked.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && !isPrivateMode()) {
        forceMask();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [forceMask]);

  useEffect(() => {
    if (!idleTimeoutMin || idleTimeoutMin <= 0) return undefined;
    const ms = idleTimeoutMin * 60 * 1000;
    let timer = null;
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!isPrivateMode()) forceMask();
      }, ms);
    };
    const events = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [idleTimeoutMin, forceMask]);

  // PrivacyContext is the sole writer to setPrivateMode in production. The
  // earlier defensive subscriber here caused an extra setState round-trip on
  // every toggle (visible lag); removed in favour of the synchronous
  // applyMask() above.

  // Memoised so consumers that don't care about specific keys (e.g. an
  // `idleTimeoutMin` change) don't re-render every other useContext caller.
  const value = useMemo(() => ({
    isMasked,
    togglePrivacy,
    forceMask,
    requireAuth,
    setRequireAuth,
    idleTimeoutMin,
    setIdleTimeout,
    reAuthOpen,
    setReAuthOpen,
    onReAuthSuccess,
    onReAuthCancel,
    revealKey,
    isKeyRevealed,
  }), [
    isMasked,
    togglePrivacy,
    forceMask,
    requireAuth,
    setRequireAuth,
    idleTimeoutMin,
    setIdleTimeout,
    reAuthOpen,
    onReAuthSuccess,
    onReAuthCancel,
    revealKey,
    isKeyRevealed,
  ]);

  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
};

export const usePrivacy = () => {
  const ctx = useContext(PrivacyContext);
  if (!ctx) {
    // Safe default outside provider — feature still works in masked=false
    // mode but nothing actually toggles. Prevents test failures.
    return {
      isMasked: false,
      togglePrivacy: () => {},
      forceMask: () => {},
      requireAuth: false,
      setRequireAuth: () => {},
      idleTimeoutMin: 0,
      setIdleTimeout: () => {},
      reAuthOpen: false,
      setReAuthOpen: () => {},
      onReAuthSuccess: () => {},
      onReAuthCancel: () => {},
      revealKey: () => {},
      isKeyRevealed: () => false,
    };
  }
  return ctx;
};

export default PrivacyContext;
